
import React, { useState, useEffect } from 'react';
import { PatientData, User, PatientGroup } from '../types';
import { CORES_CROMOTERAPIA } from '../constants';
import { checkLimits } from '../services/authService';
import { 
  AlertCircle, 
  CheckCircle2, 
  Search, 
  Loader2, 
  Sparkles, 
  Activity, 
  ShieldCheck, 
  HeartPulse, 
  Clock, 
  Baby, 
  MessageSquareText 
} from 'lucide-react';

interface Props {
  user: User;
  onSubmit: (data: PatientData) => Promise<void>;
}

const PatientForm: React.FC<Props> = ({ user, onSubmit }) => {
  const [formData, setFormData] = useState<PatientData>({
    nome: '',
    idade: 30,
    grupo: 'SADIO_AFETADO',
    sintomasSelecionados: [],
    sintomasTexto: '',
    dadosBiologicos: [],
    objetivo: 'TRATAMENTO'
  });
  const [loading, setLoading] = useState(false);
  const [isAIActive, setIsAIActive] = useState(false);

  useEffect(() => {
    try {
      // @ts-ignore
      const hasKey = typeof process !== 'undefined' && !!process?.env?.API_KEY;
      setIsAIActive(hasKey);
    } catch {
      setIsAIActive(false);
    }
  }, []);

  const apometriaOptions = [
    "Queimadura (1º Grau)",
    "Queimadura (2º ou 3º Grau)",
    "Resfriado com Purulência/Secreção",
    "Falta de Apetite",
    "Crise de Dor Aguda",
    "Gordura no Sangue / Colesterol",
    "Osteoporose / Enfraquecimento Ósseo",
    "Pós-Operatório Recente",
    "Cálculos Renais / Pedras",
    "Infecção Generalizada",
    "Miasmas Espirituais",
    "Traumatismo com Sangramento",
    "Problemas no Encéfalo",
    "Debilidade em Idoso",
    "Gestação (Apoio)",
    "Esgotamento Vital"
  ];
  
  const baseOptions = Array.from(new Set(CORES_CROMOTERAPIA.flatMap(c => c.aplicabilidades)));
  const allOptions = Array.from(new Set([...baseOptions, ...apometriaOptions])).sort();
  const [filterSymptom, setFilterSymptom] = useState('');

  const handleToggleSymptom = (symptom: string) => {
    setFormData(prev => {
      if (prev.sintomasSelecionados.includes(symptom)) {
        return { ...prev, sintomasSelecionados: prev.sintomasSelecionados.filter(s => s !== symptom) };
      }
      return { ...prev, sintomasSelecionados: [...prev.sintomasSelecionados, symptom] };
    });
  };

  const limitCheck = checkLimits(user);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.idade < 0) return;
    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
  };

  const groupOptions: { id: PatientGroup; label: string; icon: any; color: string }[] = [
    { id: 'EMERGENCIA', label: 'Emergência', icon: HeartPulse, color: 'text-red-500' },
    { id: 'CRIANCA_GESTANTE', label: 'Criança/Gestante', icon: Baby, color: 'text-blue-500' },
    { id: 'IDOSO_PROBLEMATICO', label: 'Idoso', icon: Clock, color: 'text-orange-500' },
    { id: 'SADIO_AFETADO', label: 'Harmonização', icon: ShieldCheck, color: 'text-emerald-500' },
  ];

  return (
    <div className="w-full max-w-3xl mx-auto pb-10">
      {!limitCheck.allowed && (
        <div className="mb-10 p-8 rounded-3xl bg-orange-50 dark:bg-orange-900/10 border border-orange-200 flex items-start gap-6">
          <AlertCircle className="shrink-0 mt-1 text-orange-600" size={32} />
          <p className="text-lg font-medium text-orange-700 dark:text-orange-300 leading-relaxed tracking-wide">{limitCheck.error}</p>
        </div>
      )}

      <div className="bg-chroma-card rounded-[2.5rem] shadow-xl border border-purple-200 dark:border-purple-800/50 overflow-hidden">
        <div className="p-10 border-b border-purple-200 dark:border-purple-800/50 flex flex-col md:flex-row justify-between items-center gap-6 bg-purple-50/50 dark:bg-black/30">
          <h2 className="text-3xl font-bold text-chroma-text flex items-center gap-4">
            <Sparkles className="text-chroma-accent" size={32} /> Consulta Técnica
          </h2>
          <div className={`px-6 py-2 rounded-full border text-[10px] font-bold uppercase tracking-[0.3em] ${isAIActive ? 'bg-emerald-100 text-emerald-700 border-emerald-500/40' : 'bg-gray-100 text-gray-400'}`}>
            {isAIActive ? 'IA Ativa' : 'Modo Clássico'}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-10 space-y-12">
          {/* Identificação Base */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <label className="text-[10px] font-bold text-chroma-muted uppercase mb-3 block tracking-[0.2em]">Nome do Assistido</label>
              <input type="text" required value={formData.nome} onChange={e => setFormData({...formData, nome: e.target.value})}
                className="w-full bg-purple-50/30 dark:bg-black/20 border border-purple-200 dark:border-purple-800/50 rounded-2xl p-5 text-lg text-chroma-text font-light outline-none focus:ring-4 focus:ring-chroma-accent/10 transition-all shadow-sm tracking-wide" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-chroma-muted uppercase mb-3 block tracking-[0.2em]">Idade</label>
              <input type="number" step="0.1" required value={formData.idade} onChange={e => setFormData({...formData, idade: parseFloat(e.target.value) || 0})}
                className="w-full bg-purple-50/30 dark:bg-black/20 border border-purple-200 dark:border-purple-800/50 rounded-2xl p-5 text-lg text-chroma-text font-light outline-none focus:ring-4 focus:ring-chroma-accent/10 transition-all shadow-sm tracking-wide" />
            </div>
          </div>

          {/* Grupo de Classificação */}
          <div className="space-y-6">
            <label className="text-[10px] font-bold text-chroma-muted uppercase block tracking-[0.2em]">Classificação</label>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {groupOptions.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setFormData({...formData, grupo: opt.id})}
                  className={`flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 transition-all group ${formData.grupo === opt.id ? 'bg-chroma-accent/10 border-chroma-accent shadow-md scale-[1.02]' : 'bg-purple-50/30 dark:bg-black/20 border-purple-200 dark:border-purple-800/50 hover:border-chroma-accent/40'}`}
                >
                  <opt.icon className={`${opt.color} shrink-0 group-hover:scale-110 transition-transform`} size={28} />
                  <span className="text-[11px] font-bold text-chroma-text tracking-wide">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Seleção de Queixas (Checkboxes) */}
          <div className="space-y-6">
            <label className="text-[10px] font-bold text-chroma-muted uppercase block tracking-[0.2em]">Seleção de Queixas Frequentes</label>
            <div className="bg-purple-50/30 dark:bg-black/20 rounded-3xl p-8 border border-purple-200 dark:border-purple-800/50 shadow-inner">
              <div className="relative mb-6">
                <Search className="absolute left-5 top-5 text-purple-400" size={20} />
                <input type="text" placeholder="Pesquisar condição..." className="w-full bg-white dark:bg-black/40 border border-purple-100 dark:border-purple-800/50 rounded-2xl py-4 pl-14 pr-6 text-base text-chroma-text font-light outline-none focus:ring-4 focus:ring-chroma-accent/10 shadow-sm tracking-wide"
                  value={filterSymptom} onChange={(e) => setFilterSymptom(e.target.value)} />
              </div>
              <div className="h-64 overflow-y-auto space-y-1 custom-scrollbar pr-2 grid grid-cols-1 md:grid-cols-2 gap-x-4">
                {allOptions.filter(opt => opt.toLowerCase().includes(filterSymptom.toLowerCase())).map(opt => (
                  <label key={opt} className="flex items-center gap-4 p-3 rounded-xl hover:bg-chroma-accent/10 cursor-pointer transition-all border border-transparent hover:border-chroma-accent/20 group">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${formData.sintomasSelecionados.includes(opt) ? 'bg-chroma-accent border-chroma-accent' : 'border-purple-300 dark:border-purple-700'}`}>
                      {formData.sintomasSelecionados.includes(opt) && <CheckCircle2 size={14} className="text-white" />}
                    </div>
                    <span className="text-sm font-light text-chroma-text tracking-wide truncate">{opt}</span>
                    <input type="checkbox" className="hidden" checked={formData.sintomasSelecionados.includes(opt)} onChange={() => handleToggleSymptom(opt)} />
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Detalhes Adicionais (Texto Livre) */}
          <div className="space-y-6">
            <label className="text-[10px] font-bold text-chroma-muted uppercase block tracking-[0.2em]">Detalhes Adicionais e Observações</label>
            <div className="relative">
               <MessageSquareText className="absolute left-5 top-5 text-purple-400" size={20} />
               <textarea 
                 rows={5}
                 placeholder="Descreva detalhes específicos da queixa, sensações relatadas pelo assistido ou observações de sua percepção terapêutica..."
                 className="w-full bg-purple-50/30 dark:bg-black/20 border border-purple-200 dark:border-purple-800/50 rounded-[2rem] p-5 pl-14 text-base text-chroma-text font-light outline-none focus:ring-4 focus:ring-chroma-accent/10 transition-all shadow-sm tracking-wide resize-none"
                 value={formData.sintomasTexto}
                 onChange={e => setFormData({...formData, sintomasTexto: e.target.value})}
               ></textarea>
            </div>
            <p className="text-[10px] text-chroma-muted font-medium italic ml-2">Este campo livre é fundamental para a IA personalizar as ideoplastias e protocolos.</p>
          </div>

          {/* Botão de Envio */}
          <div className="pt-6">
            <button type="submit" disabled={!limitCheck.allowed || loading}
              className="w-full py-6 bg-gradient-to-r from-chroma-accent to-purple-600 text-white rounded-[2rem] font-bold text-xl shadow-xl hover:shadow-purple-500/40 transition-all flex items-center justify-center gap-4 disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed tracking-widest uppercase">
              {loading ? <Loader2 className="animate-spin" size={28} /> : (
                <>
                  <Activity size={24} />
                  Gerar Protocolo Técnico
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PatientForm;
